import { type DeviceEntity, type DeviceId } from '@solinkcorp/api-types';
import { type UserSession, useUserSession } from '@solinkcorp/authentication';
import { toastUnexpectedError } from '@solinkcorp/solink-components';
import { TestLocationAccessibility } from 'actions/LocationReduxActions';
import { VmsApi } from 'clients';
import { useEffect, useMemo } from 'react';
import { forceDeviceLocal } from 'stores/LocationStore';
import { getReduxStore, useSelector } from 'stores/store';
import useDevices from './devices/useDevices';

/**
 * Checks to see if a given device is on the local network or not.
 *
 * Returns true if the device is local, false if remote, and undefined if
 * we don't know yet.
 */
export function useDeviceIsLocal(deviceId: DeviceId | undefined): boolean | undefined {
  const arr = useMemo(() => (deviceId ? [deviceId] : undefined), [deviceId]);
  const result = useDevicesAreLocal(arr);
  return (deviceId && result[deviceId]) || false;
}

/** Checks to see if a set of locations are on the local network or not. */
export function useDevicesAreLocal(
  deviceIds: DeviceId[] | undefined,
): Record<string, boolean | undefined> {
  const user = useUserSession();
  const locationAccessibilities = useSelector((state) => state.locationState.accessibilities);
  const { devicesById } = useDevices();
  useEffect(() => {
    if (deviceIds) {
      loadLocalLocations(user, deviceIds, devicesById).catch(toastUnexpectedError);
    }
  }, [user, deviceIds, devicesById]);

  return useMemo(() => {
    return (
      deviceIds?.reduce((byId: Record<string, boolean | undefined>, deviceId: string) => {
        byId[deviceId] = locationAccessibilities[deviceId] ?? undefined;
        return byId;
      }, {}) || {}
    );
  }, [locationAccessibilities, deviceIds]);
}

export function useAllDevicesAreLocal(deviceIds: DeviceId[] | undefined): boolean {
  const localDevices = useDevicesAreLocal(deviceIds);
  return deviceIds?.every((id) => localDevices[id]) ?? false;
}

/**
 * Given a list of device IDs, checks each to see if the device is
 * local or not and updates the device.
 */
export async function loadLocalLocations(
  user: UserSession,
  deviceIds: string[],
  devicesById: Record<string, DeviceEntity>,
): Promise<boolean[]> {
  return Promise.all(
    deviceIds.map(async (deviceId) => {
      const device = devicesById && devicesById[deviceId];
      let isAccessible = false;
      if (device) {
        isAccessible = await loadLocalDevice(user, device, cache);
      }
      return isAccessible;
    }),
  );
}

// TODO: When we have some kind of location cache, this should go there.
class DeviceIsLocalCache {
  private promiseCache: Record<string, Promise<any> | undefined> = {};

  async checkDeviceIsLocal(device: {
    id: string;
    deviceLocalUrl: string | null;
  }): Promise<boolean> {
    const { id, deviceLocalUrl } = device;

    let isLocal = false;
    if (deviceLocalUrl) {
      try {
        // Don't check the same device more than once...
        let deviceLocalPromise = this.promiseCache[id];
        if (!deviceLocalPromise) {
          deviceLocalPromise = this.promiseCache[id] = VmsApi(deviceLocalUrl, false).testAccess();
        }
        const response = await deviceLocalPromise;
        this.promiseCache[id] = undefined;

        // TODO: Why does this return something with quotes around it?
        const returnedId = response.replace(/"/g, '');
        isLocal = returnedId && returnedId === id;
      } catch (_error) {
        // Failure here means we couldn't connect locally, in which case the
        // device is not local.
        isLocal = false;
      }
    }

    return isLocal;
  }
}

const cache = new DeviceIsLocalCache();

/**
 * Verifies whether or not the passed in device is on the local network.
 *
 * This will update the Flux and Redux stores as appropriate.
 */
async function loadLocalDevice(
  user: UserSession,
  device: DeviceEntity,
  cache: DeviceIsLocalCache,
): Promise<boolean> {
  const { id: deviceId } = device;
  const { deviceLocalUrl, type: deviceType } = device;
  if (deviceType === 'data' || !deviceLocalUrl) {
    return false;
  }

  const localOnly = forceDeviceLocal(user);

  // TODO: Store the last time we checked if this device was local in addition to a true/false flag.
  const store = getReduxStore();
  const accessibleFromStore = store.getState().locationState.accessibilities[deviceId];

  let isLocal = localOnly || accessibleFromStore;
  if (isLocal === undefined) {
    // Go check the actual device...
    isLocal = await cache.checkDeviceIsLocal(device);
  }

  if (accessibleFromStore !== isLocal) {
    // Update the redux store.
    store.dispatch(TestLocationAccessibility(deviceId, isLocal));
  }

  return isLocal;
}
